import React, { PropsWithChildren, useRef, useMemo } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useParallaxTransition } from '../hooks/useParallaxTransition';

interface ScrollSequenceSectionProps extends PropsWithChildren {
  id?: string;
  backgroundClassName?: string;
  className?: string;
}

export const ScrollSequenceSection: React.FC<ScrollSequenceSectionProps> = ({
  id,
  backgroundClassName,
  className,
  children
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });

  // Enhanced parallax scroll effect using custom hook
  const { section: parallaxValues } = useParallaxTransition();

  // Create section-specific transforms that complement the global parallax
  const sectionTranslateY = useTransform(scrollYProgress, [0, 0.15, 0.3, 0.6, 0.85, 1], [150, 100, 50, 0, -50, -100]);
  const sectionOpacity = useTransform(scrollYProgress, [0, 0.1, 0.25, 0.5, 0.8, 1], [0, 0.6, 0.9, 1, 1, 0.8]);
  const sectionScale = 1;

  // Memoize background elements to prevent recreation
  const backgroundElements = useMemo(() => (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      <div
        className="absolute -top-32 left-1/2 w-[36rem] h-[36rem] -translate-x-1/2 rounded-full blur-[120px]"
        style={{
          background: 'radial-gradient(circle, rgba(14,165,233,0.12) 0%, transparent 70%)',
          opacity: 0.8,
          willChange: 'transform'
        }}
      />
      <div
        className="absolute bottom-0 right-0 w-[28rem] h-[28rem] translate-x-1/3 translate-y-1/3 rounded-full blur-[120px]"
        style={{
          background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)',
          opacity: 0.7,
          willChange: 'transform'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/40" />
      <div
        className="absolute inset-0 mix-blend-soft-light opacity-10"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)',
          backgroundSize: '5px 5px'
        }}
      />
      <div className="absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-black/70 via-black/30 to-transparent" />
      <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
    </div>
  ), []);

  return (
    <section
      id={id}
      ref={sectionRef}
      className={`relative w-full py-12 sm:py-16 overflow-hidden overflow-x-hidden parallax-safe-scale overflow-safe ${backgroundClassName ?? ''}`}
    >
      {backgroundElements}
      <motion.div
        style={{ 
          y: sectionTranslateY, 
          opacity: sectionOpacity, 
          scale: sectionScale, 
          transformOrigin: 'center center',
          willChange: 'transform, opacity' 
        }}
        className={`container mx-auto px-4 sm:px-6 ${className ?? ''}`}
      >
        {children}
      </motion.div>

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/10" />
    </section>
  );
};
